"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DemandeDevis() {
  return <section id="demandez-votre-devis" className="py-16 bg-background">
      <FormProvider formId="quote_form_home" sectionName="demandez-votre-devis" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" defaultValues={{
      name: "",
      email: "",
      phone: "",
      company: "",
      services: [],
      message: ""
    }} validationRules={{
      name: {
        required: true
      },
      email: {
        required: true,
        pattern: "email"
      },
      phone: {
        required: true,
        pattern: "phone"
      },
      services: {
        required: true
      }
    }} submitText="Envoyer la demande" successMessage="Merci ! Nous avons bien reçu votre demande de devis et vous contacterons sous 24 heures." errorMessage="Une erreur est survenue. Veuillez vérifier vos informations et réessayer.">
        <QuoteFormContent />
      </FormProvider>
    </section>;
}
function QuoteFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const leftRef = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const rightRef = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <div className="max-w-2xl mx-auto text-center">
        <Card className="p-8 bg-primary/10 border border-primary shadow-medium rounded-lg">
          <p className="text-lg text-foreground mb-6 font-medium">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="hover-lift">
            Faire une nouvelle demande
          </Button>
        </Card>
      </div>;
  }
  return <div className="grid lg:grid-cols-2 gap-12 items-start">
      {}
      <div ref={leftRef.ref} className={`space-y-6 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${leftRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <h2 className="text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight">Trouver de nouveaux clients</h2>
        <p className="text-lg text-muted-foreground leading-relaxed">Réservez votre rdv audit digital gratuit dans vos locaux, en visio ou au téléphone avec votre conseiller local:</p>
        <div className="space-y-4 pt-4 border-t border-border">
          <Button className="w-full sm:w-auto h-auto py-4 px-6 text-left whitespace-normal break-words font-semibold bg-primary text-foreground hover:bg-primary-hover hover-lift shadow-soft">Je réserve mon rdv d'audit digital gratuit dans mes locaux, en visio ou par téléphone.</Button>
          <ul className="space-y-2 text-sm text-muted-foreground"><li className="flex items-center gap-2" data-ast-id="f:/src/pages/Home/sections/DemandeDevis.tsx|sig:k20zsf" data-ast-start="3393" data-ast-end="3559" contenteditable="false" style={{}}></li></ul>
        </div>
      </div>

      {}
      <div ref={rightRef.ref} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${rightRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Card className="shadow-medium hover:shadow-strong transition-shadow duration-[var(--duration-medium)] border-none rounded-lg">
          <CardContent className="pt-8 px-6 pb-8">
            <Form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <FormField control={control} name="name" rules={{
                required: "Le nom est requis"
              }} render={({
                field
              }) => <FormItem>
                      <FormLabel>Nom / Prénom</FormLabel>
                      <FormControl>
                        <Input placeholder="Jean Dupont" {...field} className="focus-ring" />
                      </FormControl>
                      <FormDescription>Indiquez votre nom complet.</FormDescription>
                      <FormMessage />
                    </FormItem>} />

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField control={control} name="email" rules={{
                  required: "L'adresse e-mail est requise",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Adresse e-mail invalide"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel>Adresse e‑mail</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jean.dupont@example.com" {...field} className="focus-ring" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="phone" rules={{
                  required: "Le numéro de téléphone est requis"
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel>Téléphone</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+33 6 12 34 56 78" {...field} className="focus-ring" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <FormField control={control} name="company" render={({
                field
              }) => <FormItem>
                      <FormLabel>Nom de l’entreprise</FormLabel>
                      <FormControl>
                        <Input placeholder="Nom de votre société" {...field} className="focus-ring" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="services" rules={{
                required: "Veuillez sélectionner au moins un service"
              }} render={({
                field
              }) => <FormItem>
                      <FormLabel>Services souhaités</FormLabel>
                      <FormDescription>Cochez les services pour lesquels vous souhaitez un devis.</FormDescription>
                      <FormControl>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          {["SEO", "Référencement IA générative", "Google Ads", "Réseaux sociaux", "Campagnes SMS", "Création site WordPress", "Goodies", "Distribution de flyers"].map((service, i) => <label key={service} data-index={i} className="flex items-center gap-3 cursor-pointer group p-2 rounded-md hover:bg-muted/50 transition-colors">
                              <input type="checkbox" value={service} checked={(field.value as string[])?.includes(service)} onChange={e => {
                        const currentValue = field.value as string[] || [];
                        const newValue = e.target.checked ? [...currentValue, service] : currentValue.filter(s => s !== service);
                        field.onChange(newValue);
                      }} className="size-4 rounded border-border accent-primary cursor-pointer" />
                              <span className="text-sm text-foreground transition-colors">
                                {service}
                              </span>
                            </label>)}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="message" render={({
                field
              }) => <FormItem>
                      <FormLabel>Message / Besoin spécifique</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Décrivez votre projet ou vos attentes..." className="min-h-32 focus-ring resize-none" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                {submitError && <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                    <p className="text-sm text-destructive">{submitError}</p>
                  </div>}

                <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-primary text-foreground hover:bg-primary-hover hover-lift shadow-soft">
                  {isSubmitting ? <>
                      <Loader className="animate-spin mr-2 size-5" />
                      Envoi en cours...
                    </> : "Envoyer la demande"}
                </Button>
              </div>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>;
}